import {
  useEffect,
  useState,
} from "react";
import axios from "axios";

import api from "../../../lib/api";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";

interface PolizaImportes {
  vigenciaId: number | null;
  idExterno: string;
  cliente: string;
  primaNetaCents: number | null;
  impuestosCents: number | null;
  recargoCents: number | null;
  consorcioCents: number | null;
  primaTotalCents: number | null;
  comisionPorcentajeBps: number | null;
}

interface Props {
  poliza: PolizaImportes | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function getErrorMessage(
  error: unknown
): string {
  if (
    axios.isAxiosError<{
      error?: string;
    }>(error)
  ) {
    return (
      error.response?.data?.error ??
      "No se pudieron guardar los importes"
    );
  }

  return "No se pudieron guardar los importes";
}

function centsToInput(
  value: number | null
): string {
  if (value === null) return "";

  return (value / 100).toFixed(2);
}

function bpsToInput(
  value: number | null
): string {
  if (value === null) return "";

  return String(value / 100);
}

export function CrmEditarImportesModal({
  poliza,
  onClose,
  onSaved,
}: Props) {
  const [primaNeta, setPrimaNeta] =
    useState("");
  const [impuestos, setImpuestos] =
    useState("");
  const [recargo, setRecargo] =
    useState("");
  const [consorcio, setConsorcio] =
    useState("");
  const [primaTotal, setPrimaTotal] =
    useState("");
  const [
    comisionPorcentaje,
    setComisionPorcentaje,
  ] = useState("");

  const [error, setError] = useState("");
  const [saving, setSaving] =
    useState(false);

  useEffect(() => {
    setPrimaNeta(
      centsToInput(
        poliza?.primaNetaCents ?? null
      )
    );
    setImpuestos(
      centsToInput(
        poliza?.impuestosCents ?? null
      )
    );
    setRecargo(
      centsToInput(
        poliza?.recargoCents ?? null
      )
    );
    setConsorcio(
      centsToInput(
        poliza?.consorcioCents ?? null
      )
    );
    setPrimaTotal(
      centsToInput(
        poliza?.primaTotalCents ?? null
      )
    );
    setComisionPorcentaje(
      bpsToInput(
        poliza?.comisionPorcentajeBps ??
          null
      )
    );
    setError("");
  }, [poliza]);

  const closeModal = () => {
    if (!saving) onClose();
  };

  const saveImportes = async () => {
    if (!poliza?.vigenciaId) {
      setError(
        "Esta póliza no tiene una vigencia editable."
      );
      return;
    }

    if (
      [
        primaNeta,
        impuestos,
        recargo,
        consorcio,
        primaTotal,
        comisionPorcentaje,
      ].some((value) => !value.trim())
    ) {
      setError(
        "Completa todos los datos financieros."
      );
      return;
    }

    setError("");
    setSaving(true);

    try {
      await api.patch(
        `/admin/crm/polizas/vigencias/${poliza.vigenciaId}/importes`,
        {
          prima_neta: primaNeta,
          impuestos,
          recargo,
          consorcio,
          prima_total: primaTotal,
          comision_porcentaje:
            comisionPorcentaje,
        }
      );

      await onSaved();
    } catch (requestError) {
      setError(
        getErrorMessage(requestError)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={poliza !== null}
      onClose={closeModal}
      title={`Importes de ${
        poliza?.idExterno ?? "póliza"
      }`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            onClick={saveImportes}
            loading={saving}
          >
            Guardar importes
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p>
            Cliente:{" "}
            <strong>
              {poliza?.cliente}
            </strong>
          </p>

          <p className="mt-1">
            La comisión se calculará
            automáticamente sobre la prima
            neta.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Prima neta (€) *"
            type="number"
            min="0"
            step="0.01"
            value={primaNeta}
            onChange={(event) =>
              setPrimaNeta(
                event.target.value
              )
            }
          />

          <Input
            label="Impuestos (€) *"
            type="number"
            min="0"
            step="0.01"
            value={impuestos}
            onChange={(event) =>
              setImpuestos(
                event.target.value
              )
            }
          />

          <Input
            label="Recargo (€) *"
            type="number"
            min="0"
            step="0.01"
            value={recargo}
            onChange={(event) =>
              setRecargo(
                event.target.value
              )
            }
          />

          <Input
            label="Consorcio (€) *"
            type="number"
            min="0"
            step="0.01"
            value={consorcio}
            onChange={(event) =>
              setConsorcio(
                event.target.value
              )
            }
          />

          <Input
            label="Prima total (€) *"
            type="number"
            min="0"
            step="0.01"
            value={primaTotal}
            onChange={(event) =>
              setPrimaTotal(
                event.target.value
              )
            }
          />

          <Input
            label="Comisión sobre prima neta (%) *"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={comisionPorcentaje}
            onChange={(event) =>
              setComisionPorcentaje(
                event.target.value
              )
            }
          />
        </div>
      </div>
    </Modal>
  );
}